import { useState } from "react";
import { Button } from "@/design-system/primitives";
import { useAuth } from "@/lib/auth/AuthProvider";
import { usePendingDoseChangeHolds } from "@/lib/api/doseChangeHolds";
import { DoseChangeReviewSheet, PRESCRIBER_ONLY } from "./DoseChangeReviewSheet";
import { ClearDoseChangeRequest } from "./ClearDoseChangeRequest";
import { AlreadyHandledDoseChange } from "./AlreadyHandledDoseChange";
import { NoDoseChangeAvailable } from "./NoDoseChangeAvailable";
import { DoseRequestMessage } from "./DoseRequestMessage";

/** Oldest requests shown before "View all"; the full page can run to dozens. */
const PREVIEW = 3;

export function PendingDoseChangeHolds() {
  const { session, loading } = useAuth();
  if (loading || !session) return null;
  return <PendingList key={session.user.id} />;
}
function PendingList() {
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const query = usePendingDoseChangeHolds(offset);
  if (query.isError)
    return (
      <p role="alert" className="text-14 text-danger">
        Pending dose-change reviews could not be checked.{" "}
        <button className="underline" onClick={() => void query.refetch()}>
          Retry pending reviews
        </button>
      </p>
    );
  if (query.isPending)
    return (
      <p role="status" className="text-14 text-ink-muted">
        Checking pending dose-change reviews…
      </p>
    );
  const data = query.data;
  if (!data || (!data.enabled && !data.items.length && offset === 0))
    return null;
  // Supervisors and nurses see the whole queue to triage it; only the
  // patient's prescriber can review. An older server omits the flag.
  const canReview = data.can_resolve !== false;
  const collapsed = !expanded && data.items.length > PREVIEW;
  const shown = collapsed ? data.items.slice(0, PREVIEW) : data.items;
  // The server pages 50 at a time and has no overall count: number the whole
  // queue only from the first page, and only "+" when more pages follow.
  const paged = offset > 0 || data.has_more;
  const total = offset === 0 ? `${data.items.length}${data.has_more ? "+" : ""}` : null;
  return (
    <section
      aria-label="Pending dose-change reviews"
      className="border border-warning/40 bg-warning-wash p-15 flex flex-col gap-10"
    >
      <h2 className="type-label-sm font-medium">
        Pending dose-change reviews{data.items.length && total ? ` (${total})` : ""}
      </h2>
      <p className="text-14">
        Patient requests awaiting a clinical decision. Their fills are on hold
        until you keep the current dose or change it.
      </p>
      {shown.length ? (
        shown.map((hold) => (
          <article key={hold.id} className="border-t border-line pt-10">
            <div className="flex flex-wrap items-center justify-between gap-10">
            <div className="min-w-0">
              <p className="text-14 font-medium">
                {hold.patient_display_name || "Patient"} ·{" "}
                {hold.product_id.replaceAll("-", " ")}
              </p>
              <p className="text-12">
                {new Date(hold.created_at).toLocaleString()} ·{" "}
                {hold.source_channel.replaceAll("_", " ")}
              </p>
              <p className="text-12">
                Prescriber:{" "}
                {hold.assigned_prescriber_name ||
                  (hold.assigned_prescriber_id
                    ? "Assigned prescriber"
                    : "Unassigned")}
              </p>
            </div>
            {canReview ? (
              <button
                type="button"
                className="underline text-14 min-h-44"
                aria-expanded={openId === hold.id}
                onClick={() =>
                  setOpenId((current) => (current === hold.id ? null : hold.id))
                }
              >
                {openId === hold.id ? "Close review" : "Review request"}
              </button>
            ) : (
              <p className="text-12 text-ink-muted">
                {PRESCRIBER_ONLY}
              </p>
            )}
            </div>
            {openId !== hold.id && (
              <div className="pt-8">
                <DoseRequestMessage hold={hold} />
              </div>
            )}
            {(hold.folded?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-5 pt-8">
                <p className="text-12">Also asked (folded into this request):</p>
                {hold.folded!.map((f) => (
                  <blockquote
                    key={f.id}
                    className="text-14 whitespace-pre-wrap break-words border-l-2 border-line pl-8"
                  >
                    {f.source_message}
                    <span className="block text-12 text-ink-muted">
                      {f.source_channel.replaceAll("_", " ")} ·{" "}
                      {new Date(f.created_at).toLocaleString()}
                    </span>
                  </blockquote>
                ))}
              </div>
            )}
            {canReview && openId === hold.id && <DoseChangeReviewSheet hold={hold} />}
            {data.can_withdraw && (
              <div className="pt-8 flex flex-col gap-8">
                <AlreadyHandledDoseChange
                  key={`handled:${hold.id}:${hold.resolution_version}`}
                  hold={hold}
                  auto={false}
                />
                <NoDoseChangeAvailable
                  key={`no-change:${hold.id}:${hold.resolution_version}`}
                  hold={hold}
                  folded={hold.folded}
                />
                <ClearDoseChangeRequest
                  key={`${hold.id}:${hold.resolution_version}`}
                  hold={hold}
                  others={data.items.filter(
                    (other) => other.patient_id === hold.patient_id,
                  )}
                />
              </div>
            )}
          </article>
        ))
      ) : (
        <p className="text-14">No pending requests on this page.</p>
      )}
      {data.items.length > PREVIEW && (
        <Button
          type="button"
          variant="secondary"
          className="self-start"
          aria-expanded={!collapsed}
          onClick={() => {
            if (!collapsed) setOffset(0);
            setExpanded(collapsed);
          }}
        >
          {collapsed
            ? paged
              ? `View all ${data.items.length} on this page`
              : `View all ${data.items.length}`
            : "Show fewer"}
        </Button>
      )}
      {!collapsed && (offset > 0 || data.has_more) && (
        <div className="flex gap-10">
          <Button
            type="button"
            variant="secondary"
            disabled={offset === 0 || query.isFetching}
            onClick={() => setOffset((value) => Math.max(0, value - 50))}
          >
            Previous reviews
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!data.has_more || query.isFetching}
            onClick={() => setOffset((value) => value + 50)}
          >
            Next reviews
          </Button>
        </div>
      )}
    </section>
  );
}
